import { IconShieldCheck, IconZoomOutline } from '@iwoca/icons';
import { Accordion, Input, Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import Cookies from 'js-cookie';

import styles from './BankSelectionModal.module.css';
import { useIsInDEOpenBankingCopyTest } from '../../../../hooks/useIsInDEOpenBankingCopyTest';
import { useLocale } from '../../../../hooks/useLocale';
import { TOnlineBank } from '../OpenBanking.types';
import TrueLayerShareableLink from '../TrueLayerShareableLink/TrueLayerShareableLink';

export const DE_OB_CONSENT_MESSAGE_COOKIE = 'DE_OB_CONSENT_MESSAGE';

const BankListView = ({
  banks,
  onSearch,
  searchTerm,
  onBankSelect,
  ableToShareLink,
}: {
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBankSelect: (bank: TOnlineBank) => void;
  banks: TOnlineBank[];
  searchTerm: string;
  ableToShareLink: boolean;
}) => {
  const { t } = useLingui();
  const { isUK, isDE } = useLocale();

  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  return (
    <>
      {isUK && ableToShareLink && (
        <>
          <TrueLayerShareableLink noBankLogin />
          <hr className={styles.divider} />
        </>
      )}
      <div className={styles.bankListDescription}>
        {isIncludedInOBCopyTest ? (
          <Trans>
            Use your bank’s portal to easily and securely share your last 3
            months of account statements. Your login details will not be shared
            with us.
          </Trans>
        ) : (
          <>
            <div className={styles.bankListHeader}>
              <h3 className={styles.bankListTitle}>
                <Trans>How does it work?</Trans>
              </h3>
            </div>
            <Trans>
              Securely share your bank account’s data. Simply choose your bank
              below and then log in through your bank’s portal when prompted.
            </Trans>
          </>
        )}
        {isDE && Cookies.get(DE_OB_CONSENT_MESSAGE_COOKIE) === 'true' && (
          <Accordion
            small
            frame={false}
            // @ts-expect-error orion doesn't type `defaultOpen` on the legacy `Accordion`, only on the new `AccordionItem`; it works via headlessui's prop spread
            defaultOpen
            className={styles.accordion}
            titleClassName={styles.bankListTitle}
            title={t`Consent to data use`}
          >
            <p className={styles.accordionBody}>
              <Trans>
                By linking your bank account, you explicitly consent to iwoca
                accessing up to 24 months of your transaction data via our
                partner finAPI or TrueLayer, to assess your creditworthiness.
                You can withdraw your consent at any time.
              </Trans>
            </p>
          </Accordion>
        )}
        <div className={styles.bankGradeContainer}>
          <IconShieldCheck className={styles.bankGradeIcon} />
          <span>
            <Trans>Bank-grade encryption</Trans>
          </span>
        </div>
      </div>
      <Input
        iconLeft={<IconZoomOutline />}
        className={styles.searchBar}
        placeholder={t`Search for your bank...`}
        onChange={onSearch}
        value={searchTerm}
      />
      <div
        className={clsx(styles.banksGridContainer, {
          [styles.bankButtonError]: banks.length < 1,
        })}
      >
        {banks?.map((bank) => {
          return (
            <button
              key={bank.bank_id}
              className={styles.bankButton}
              onClick={() => onBankSelect(bank)}
            >
              <figure className={styles.bankButtonFigure}>
                <img
                  src={bank.logo_url}
                  alt={bank.bank_name}
                  className={clsx('fs-mask dd-privacy-mask', styles.bankLogo)}
                />
                <figcaption className={styles.bankName}>
                  {bank.bank_name}
                </figcaption>
              </figure>
            </button>
          );
        })}
        {banks.length < 1 && (
          <p className={styles.bankListError}>
            <Trans>
              Unfortunately the bank you have chosen is not supported.
            </Trans>
          </p>
        )}
      </div>
      {!isDE && (
        <div
          className={styles.trueLayerFooter}
          data-testid="true-layer-modal-footer"
        >
          <p>
            <small>
              We’re{' '}
              <Link
                data-testid="fca-regulated-link"
                href="https://www.fca.org.uk/about/what-we-do/the-fca"
                target="_blank"
                rel="noopener noreferrer"
              >
                FCA registered
              </Link>{' '}
              which allows us to offer Open Banking
            </small>
          </p>
        </div>
      )}
    </>
  );
};

export default BankListView;
